/**
 * useFCAData — Fetches FCA result and summary data for a startup
 * Authority: FCA_EXECUTION_SPEC.md (Section 2)
 * API: GET /api/pitch-deck/{startupId}/fca/{triggerType}
 *
 * Returns the FCA result for the selected trigger, a summary object
 * indicating which triggers have completed FCAs, a loading boolean,
 * and an error string.
 */

import { useState, useEffect, useCallback } from 'react';
import { getFCAResult } from '@/lib/api';

const TRIGGER_KEYS = ['pre', 'post', 'kb'];

export default function useFCAData(startupId, triggerType) {
  const [fcaResult, setFcaResult] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build summary by probing all three trigger types
  const fetchSummary = useCallback(async (sid) => {
    const entries = await Promise.all(
      TRIGGER_KEYS.map(async (key) => {
        try {
          const data = await getFCAResult(sid, key);
          return [key, data?.status === 'complete'];
        } catch {
          return [key, false];
        }
      }),
    );
    return Object.fromEntries(entries);
  }, []);

  // Fetch summary only when startupId changes (avoids 3 redundant calls on trigger switch)
  useEffect(() => {
    if (!startupId) {
      setSummary(null);
      return;
    }
    let cancelled = false;
    fetchSummary(startupId).then((data) => {
      if (!cancelled) setSummary(data);
    });
    return () => { cancelled = true; };
  }, [startupId, fetchSummary]);

  // Fetch result when startupId or triggerType changes
  useEffect(() => {
    if (!startupId || !triggerType) {
      setFcaResult(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const resultData = await getFCAResult(startupId, triggerType);
        if (cancelled) return;
        setFcaResult(resultData?.status === 'complete' ? resultData : null);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load First Cut Analysis');
          setFcaResult(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [startupId, triggerType]);

  // Expose manual refresh for summary — called after FCA pipeline completes
  const refreshSummary = useCallback(() => {
    if (!startupId) return;
    fetchSummary(startupId).then(setSummary);
  }, [startupId, fetchSummary]);

  return { fcaResult, summary, loading, error, refreshSummary };
}
